<script setup>
import { ref, watch } from 'vue';
import { useClickAway } from '@/utils/useClickAway';
import MyLoadsDesktopButtonComponent from '@/components/myLoads/MyLoadsDesktopButtonComponent.vue';
// import { getStopDay, getTimeFromNow } from '@/utils/time';
const { externalLoadId, eldVehicles, allVehicles, isLoading, assignedVehicle } =
  defineProps({
    externalLoadId: {
      type: String,
      required: true,
    },
    actionType: {
      type: String,
      required: true,
    },
    eldVehicles: {
      type: Array,
      required: true,
      default: () => [],
    },
    allVehicles: {
      type: Array,
      required: true,
      default: () => [],
    },
    assignedVehicle: {
      type: Object,
      required: true,
      default: () => ({
        id: '',
        truck_number: '',
        vin: '',
      }),
    },
    isLoading: {
      type: Boolean,
      required: true,
      default: false,
    },
  });
const showInput = ref(true);
const filteredVehicles = ref([]);
const vehicleInput = ref('');
watch(
  () => eldVehicles,
  (newVal) => {
    filteredVehicles.value = newVal;
    vehicleInput.value = '';
  },
  { immediate: true, deep: true }
);
watch(
  () => assignedVehicle,
  (newVal) => {
    if (newVal.id) {
      showInput.value = false;
    }
  },
  { immediate: true }
);
const modalRef = ref(null);
const emit = defineEmits([
  'closeModal',
  'assignVehicle',
  'openViewEquipmentModal',
  'confirmVehicle',
]);
useClickAway(modalRef, () => emit('closeModal'));
const handleVehicleInputChange = (e) => {
  vehicleInput.value = e.target.value;
  filteredVehicles.value = eldVehicles.filter(
    ({ identifier, vin }) =>
      identifier
        .toString()
        .toLowerCase()
        .replace(/\s+/g, '')
        .includes(
          e.target.value.toString().toLowerCase().replace(/\s+/g, '')
        ) ||
      vin
        .toString()
        .toLowerCase()
        .replace(/\s+/g, '')
        .includes(e.target.value.toString().toLowerCase().replace(/\s+/g, ''))
  );
};
const handleEnterPress = (e) => {
  if (filteredVehicles.value.length === 1) {
    isOpen.value = false;
    e.target.blur();
    // emit('hoverOff', filteredVehicles.value[0].id);
    emit(
      'assignVehicle',
      filteredVehicles.value[0].id,
      filteredVehicles.value[0].identifier
    );
  }
};
const handleSelect = (vehicle) => {
  isOpen.value = false;
  emit('assignVehicle', vehicle.id, vehicle.identifier);
};
const isOpen = ref(true);
const confirmVehicle = () => {
  isOpen.value = false;
  emit('confirmVehicle');
};
</script>

<template>
  <div
    ref="modalRef"
    class="flex min-w-fit rounded-[5px] bg-white p-6 shadow-[0px_5px_10px_0px_rgba(109,113,129,0.14)]"
  >
    <div class="flex w-full flex-col gap-4">
      <div class="flex w-full items-center justify-between gap-2">
        <div class="flex flex-col gap-2">
          <MyLoadsDesktopButtonComponent
            button-class="subtle-button rounded-[5px]"
            button-type="button"
            @button-click="$emit('closeModal')"
          >
            <template #leftIcon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M18.3639 7.05044L16.9497 5.63623L11.9999 10.586L7.0502 5.63623L5.63599 7.05044L10.5857 12.0002L5.63599 16.9499L7.0502 18.3642L11.9999 13.4144L16.9497 18.3642L18.3639 16.9499L13.4141 12.0002L18.3639 7.05044Z"
                  fill="#6C7C9D"
                />
              </svg>
            </template>
          </MyLoadsDesktopButtonComponent>
          <div class="text-xl font-medium capitalize text-steel-1100">
            {{ `${actionType} Vehicle` }}
          </div>
          <div class="text-sm font-medium text-steel-1100">
            Load ID: {{ externalLoadId }}
          </div>
          <div
            v-if="actionType === 'confirm'"
            class="text-sm font-medium text-steel-1100"
          >
            Truck Number:
            {{ assignedVehicle.truck_number || 'Not found' }}
          </div>
          <div
            v-if="actionType === 'confirm'"
            class="text-sm font-medium text-steel-1100"
          >
            VIN: {{ assignedVehicle.vin || 'Not found' }}
          </div>
          <div
            v-if="actionType === 'confirm'"
            class="mt-2 flex items-center justify-between gap-1"
          >
            <MyLoadsDesktopButtonComponent
              button-class="primary-button rounded-[5px] px-3 py-1 h-8"
              text-class="text-xs font-medium leading-10 tracking-tight"
              :button-text="`Confirm Vehicle`"
              @button-click="
                (e) => {
                  e.stopPropagation();
                  confirmVehicle();
                }
              "
            />
            <MyLoadsDesktopButtonComponent
              :is-disabled="false"
              button-class="default-button rounded-[5px] px-3 py-1 h-8"
              text-class="text-xs font-medium leading-10 tracking-tight"
              :button-text="`Assign New Vehicle`"
              @button-click="
                (e) => {
                  e.stopPropagation();
                  showInput = true;
                }
              "
            />
          </div>
        </div>
      </div>
      <div
        v-if="showInput"
        class="flex flex-col gap-2"
      >
        <!-- loading vehicles -->
        <div
          v-if="isLoading"
          class="mx-auto h-16 w-16 text-center"
        >
          <img
            class="loading relative w-full"
            src="@/assets/loading-v2-yellow.svg"
            alt="loading"
          />
        </div>
        <!-- input search -->
        <!-- picklist -->
        <button
          v-if="allVehicles.length"
          class="flex min-w-fit items-center justify-start font-['Poppins'] text-xs font-medium leading-none tracking-tight text-yellow-700 underline hover:text-yellow-800 active:text-yellow-900"
          @click="$emit('openViewEquipmentModal')"
        >
          I don't see my vehicle
        </button>
        <div
          v-if="eldVehicles.length === 0"
          class="text-rose-800"
        >
          No ELD connected vehicles found.
        </div>
        <div
          v-else
          class="relative mt-1 flex w-56 flex-col gap-3"
          @click.stop
        >
          <div
            class="relative flex w-full cursor-default flex-col rounded-lg bg-white text-left shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 focus-visible:ring-offset-black sm:text-sm"
          >
            <div
              class="flex w-full items-center gap-2 rounded bg-gray-100"
              :class="isOpen ? 'ring-neutral-ring ring-1' : ''"
            >
              <input
                id="truck-number-input"
                aria-expanded="true"
                aria-autocomplete="off"
                autocomplete="off"
                role="combobox"
                type="text"
                tabindex="0"
                class="flex w-72 items-center justify-between rounded border-none bg-gray-100 py-2 pl-2 text-xs leading-5 text-gray-900"
                aria-labelledby="combobox-button"
                aria-controls="combobox-options"
                :value="vehicleInput"
                placeholder="Search Truck Number or VIN"
                @input="handleVehicleInputChange($event)"
                @click="isOpen = true"
                @keyup.enter="handleEnterPress($event)"
              />
              <button
                id="combobox-button"
                type="button"
                tabindex="-1"
                aria-haspopup="listbox"
                :aria-expanded="isOpen"
                class="flex items-center pr-2"
                aria-controls="combobox-options"
                @click="isOpen = !isOpen"
              >
                <svg
                  :class="isOpen ? '' : '-rotate-90 transform'"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 6L8 10L12 6"
                    stroke="#29303D"
                    stroke-width="2"
                  ></path>
                </svg>
              </button>
            </div>
          </div>
          <div v-if="isOpen">
            <ul
              aria-labelledby="combobox-button"
              role="listbox"
              class="ring-neutral-ring show-scroll absolute mt-1 max-h-60 w-full overflow-scroll rounded-md bg-white py-1 text-base shadow-lg ring-1 focus:outline-none sm:text-sm"
              :class="filteredVehicles.length === 0 ? 'hidden' : 'block'"
            >
              <!--             @mouseenter="emit('hoverOn', vehicle.id)"
            @mouseleave="emit('hoverOff', vehicle.id)" -->
              <!-- :class="{
                'cursor-pointer hover:bg-gray-100':
                  vehicle.id !== assignedVehicleId,
                'bg-gray-100':
                  filteredVehicles.length === 1 &&
                  vehicle.id !== assignedVehicleId,
              }" -->
              <!-- filter out assignedVehicle -->
              <li
                v-for="vehicle in filteredVehicles"
                :key="vehicle.id"
                class="flex cursor-pointer select-none items-center gap-1 px-4 py-2 text-gray-900 hover:bg-gray-100"
                :class="{
                  'bg-gray-100': filteredVehicles.length === 1,
                }"
                role="option"
                tabindex="-1"
                @click="handleSelect(vehicle)"
              >
                <div class="flex w-40 flex-col justify-center text-xs">
                  <div
                    class="text-info-bold-text flex w-full gap-0.5 break-all text-sm font-medium leading-tight tracking-tight"
                  >
                    {{ ' ' + vehicle.identifier }}
                  </div>
                  <div
                    class="text-info-subtle-text flex w-full items-center break-keep font-medium leading-3 tracking-tight"
                  >
                    {{ vehicle.vin }}
                  </div>
                  <!-- <div
                  v-if="vehicle.isUnverified"
                  class="unverified-bg mr-1 h-4 w-min gap-0.5 break-normal rounded px-1 text-xs text-white"
                >
                  Unverified
                </div> -->
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.show-scroll {
  overflow: scroll;
  scrollbar-width: 2px;
}
/* .show-scroll::-webkit-scrollbar {
  display: block;
  scrollbar-width: 2px;
} */
</style>
